import { useEffect, useState } from 'react';
import type { PurchaseInvoiceExpenseDetailRow, PurchaseInvoiceFieldValue } from '../../../types';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import { DetailRow } from './useDetailRows';
import { ChiPhiRow } from './useChiPhiRows';

export interface ChiPhiChiTietRow extends PurchaseInvoiceExpenseDetailRow {
  chi_phi_uuid?: string | null;
  detail_uuid?: string | null;
}

export interface SelectedChiPhiChiTietCellInfo {
  id: string;
  field: string;
}

const buildAllocatedRowUuid = (chiPhiRow: ChiPhiRow, detailRow: DetailRow) =>
  ['cpct', chiPhiRow.uuid || chiPhiRow.line || 'cp', detailRow.uuid || detailRow.line || 'vt'].join('-');

const areRowsEqual = (left: ChiPhiChiTietRow[], right: ChiPhiChiTietRow[]) => {
  if (left.length !== right.length) return false;

  return left.every((leftRow, index) => {
    const rightRow = right[index];
    if (!rightRow) return false;

    const keys = Array.from(new Set([...Object.keys(leftRow), ...Object.keys(rightRow)]));

    return keys.every(key => leftRow[key] === rightRow[key]);
  });
};

export function useChiPhiChiTietRows(
  initialRows: ChiPhiChiTietRow[] = [],
  chiPhiRows: ChiPhiRow[] = [],
  detailRows: DetailRow[] = []
) {
  const [rows, setRows] = useState<ChiPhiChiTietRow[]>(initialRows);
  const [selectedRowUuid, setSelectedRowUuid] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<ChiPhiChiTietRow | null>(null);
  const [selectedCell, setSelectedCell] = useState<SelectedChiPhiChiTietCellInfo | null>(null);

  useEffect(() => {
    if (chiPhiRows.length > 0 && detailRows.length > 0) {
      const newChiTietRows = chiPhiRows.flatMap(chiPhiRow => {
        const loai_pb = String(chiPhiRow?.ma_cp_data?.loai_pb);
        const chiPhiAmount = Number(chiPhiRow.tien_cp_nt || 0);

        if (loai_pb === '1') {
          const totalQuantity = detailRows.reduce((sum, row) => sum + Number(row?.so_luong || 0), 0);
          if (totalQuantity <= 0) return [];

          return detailRows.map(detailRow => {
            const quantity = Number(detailRow?.so_luong || 0);
            const allocatedAmount =
              totalQuantity > 0 ? Math.round(((chiPhiAmount * quantity) / totalQuantity) * 100) / 100 : 0;

            return {
              ma_cp_data: chiPhiRow.ma_cp_data,
              ma_vt_data: detailRow.ma_vt_data,
              tien_cp_nt: allocatedAmount,
              line_vt: detailRow.line,
              line_cp: chiPhiRow.line,
              chi_phi_uuid: chiPhiRow.uuid,
              detail_uuid: detailRow.uuid,
              uuid: buildAllocatedRowUuid(chiPhiRow, detailRow)
            };
          });
        } else if (loai_pb === '4') {
          const totalBaseAmount = detailRows.reduce((sum, row) => {
            const baseAmount = Number(row?.tien_tck_nt || 0) || Number(row?.tien_nt0 || 0);
            return sum + baseAmount;
          }, 0);

          if (totalBaseAmount <= 0) return [];

          return detailRows.map(detailRow => {
            const baseAmount = Number(detailRow?.tien_tck_nt || 0) || Number(detailRow?.tien_nt0 || 0);
            const allocatedAmount =
              totalBaseAmount > 0 ? Math.round(((chiPhiAmount * baseAmount) / totalBaseAmount) * 100) / 100 : 0;

            return {
              ma_cp_data: chiPhiRow.ma_cp_data,
              ma_vt_data: detailRow.ma_vt_data,
              tien_cp_nt: allocatedAmount,
              line_vt: detailRow.line,
              line_cp: chiPhiRow.line,
              chi_phi_uuid: chiPhiRow.uuid,
              detail_uuid: detailRow.uuid,
              uuid: buildAllocatedRowUuid(chiPhiRow, detailRow)
            };
          });
        } else {
          return [];
        }
      });
      setRows(prevRows => (areRowsEqual(prevRows, newChiTietRows) ? prevRows : newChiTietRows));
    } else {
      setRows(prevRows => (prevRows.length === 0 ? prevRows : []));
    }
  }, [chiPhiRows, detailRows]);

  const handleAddRow = () => {
    const maxLine = rows.reduce((max, row) => Math.max(max, row.line || 0), 0);
    const newRow = {
      uuid: String(Math.random()),
      line: maxLine + 1
    };

    setRows([...rows, newRow]);

    // Select the newly added row
    setSelectedRowUuid(newRow.uuid);
    setSelectedRow(newRow);
  };

  const handleDeleteRow = () => {
    let updatedRows: ChiPhiChiTietRow[] = [];

    if (selectedRowUuid) {
      updatedRows = rows.filter(row => row.uuid !== selectedRowUuid);
      setRows(updatedRows);
    } else if (rows.length > 0) {
      updatedRows = rows.slice(0, -1);
      setRows(updatedRows);
    }

    // If there are remaining rows, select the last one
    if (updatedRows.length > 0) {
      const lastRow = updatedRows[updatedRows.length - 1];
      setSelectedRowUuid(lastRow.uuid || null);
      setSelectedRow(lastRow);
    } else {
      // If no rows remain, clear selection
      clearSelection();
    }
  };

  const handleCopyRow = () => {
    if (!selectedRow) return;

    const { uuid, ...rowDataWithoutUuid } = selectedRow;

    const newRow = {
      ...rowDataWithoutUuid,
      uuid: String(Math.random())
    };

    setRows([...rows, newRow]);

    // Select the newly copied row
    setSelectedRowUuid(newRow.uuid);
    setSelectedRow(newRow);
  };

  const handlePasteRow = () => {
    if (!selectedRow) return;

    const { uuid, ...rowDataWithoutUuid } = selectedRow;

    let newRow = {
      ...rowDataWithoutUuid,
      uuid: String(Math.random())
    };

    if (selectedRowUuid) {
      const selectedIndex = rows.findIndex(row => row.uuid === selectedRowUuid);
      if (selectedIndex === -1) return;

      const newRows = [...rows];
      newRows.splice(selectedIndex + 1, 0, newRow);

      setRows(newRows);
    } else {
      setRows([...rows, newRow]);
    }

    // Select the newly pasted row
    setSelectedRowUuid(newRow.uuid);
    setSelectedRow(newRow);
  };

  const handleMoveRow = (direction: 'up' | 'down') => {
    if (!selectedRowUuid) return;

    const currentIndex = rows.findIndex(row => row.uuid === selectedRowUuid);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? Math.max(0, currentIndex - 1) : Math.min(rows.length - 1, currentIndex + 1);

    if (newIndex === currentIndex) return;

    const newRows = [...rows];
    const [movedRow] = newRows.splice(currentIndex, 1);
    newRows.splice(newIndex, 0, movedRow);

    setRows(newRows);

    // Ensure selection follows the moved row
    // No need to update selectedRowUuid as it remains the same
    setSelectedRow(movedRow);
  };

  const handleCellValueChange = (rowUuid: string, field: string, newValue: PurchaseInvoiceFieldValue) => {
    const rowIndex = rows.findIndex(row => row.uuid === rowUuid);
    if (rowIndex === -1) return;
    if (rows[rowIndex]?.[field] === newValue) return;

    const updatedRows = [...rows];
    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      [field]: newValue
    };

    setRows(updatedRows);

    // If the changed row is the currently selected row, update selectedRow
    if (rowUuid === selectedRowUuid) {
      setSelectedRow(updatedRows[rowIndex]);
    }
  };

  const handleRowClick = (params: { id: string; row: ChiPhiChiTietRow }) => {
    const rowUuid = params.id || params.row?.uuid;
    if (!rowUuid) return;

    setSelectedRowUuid(rowUuid);
    setSelectedRow(params.row);
  };

  const handleCellClick = (params: DataTableRenderCellParams) => {
    const rowUuid = params.id.toString();
    if (!rowUuid) return;

    // Update row selection
    setSelectedRowUuid(rowUuid);
    setSelectedRow(params.row as ChiPhiChiTietRow);

    // Update cell selection
    setSelectedCell({
      id: rowUuid,
      field: params.field
    });
  };

  const clearSelection = () => {
    setSelectedRowUuid(null);
    setSelectedRow(null);
    setSelectedCell(null);
  };

  return {
    rows,
    setRows,
    selectedRowUuid,
    selectedRow,
    selectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    handleCellValueChange
  };
}
